<script lang="ts">
import { useRouter } from 'vue-router'
import { defineComponent } from 'vue'
import PageBreadcrumb from '@/components/common/PageBreadcrumb.vue'
import MailDataService from '@/services/MailDataService'
import type ResponseData from '@/types/ResponseData'
import type { LaravelPaginatedResponse } from '@/types/ApiResponses'
import type Mail from '@/types/Mail'
import type ObjectConfig from '@/types/ObjectConfig'
import * as _ from 'lodash'
import { toast } from 'vue3-toastify'
import 'vue3-toastify/dist/index.css'
import { Table, TableHeader, TableBody, TableRow, TableCell } from '@/components/ui/table'
import { TableCard, TableSearch, TablePagination, TableEmptyState, TableFilterDropdown } from '@/components/common/table'
import { Checkbox } from '@/components/ui/checkbox'
import { Button } from '@/components/ui/button'
import { CircleCheck, Plus } from 'lucide-vue-next'

export default defineComponent({
  name: 'mail-list', data() {
    return {
      mails: [] as Mail[],
      router: useRouter(),
      filterMails: '',
      loaded: false,
      selectedColumn: 'id',
      selectedDirection: 'desc',
      pagination: {} as LaravelPaginatedResponse<Mail>['data'],
      mailStatuses: [] as ObjectConfig[],
      selectedMailIds: [] as number[],
      bulkSelectedStatus: '',
      isBulkUpdating: false,
      searchTimer: undefined as ReturnType<typeof setTimeout> | undefined,
    }
  },
  components: {
    Table,
    TableHeader,
    TableBody,
    TableRow,
    TableCell,
    TableCard,
    TableSearch,
    TablePagination,
    TableEmptyState,
    TableFilterDropdown,
    Checkbox,
    Button,
    CircleCheck,
    Plus,
    PageBreadcrumb,
  },
  methods: {
    retrieveMails(page: number, type: string) {
      this.selectedMailIds = [];
      if (type == 'prev') {
        page--;
      } else if (type == 'next') {
        page++;
      } MailDataService.getAllMails(page, this.filterMails)
        .then((response: ResponseData) => {
          this.mails = response.data.data.data;
          this.pagination = response.data.data;
          this.loaded = true
        })
        .catch((e: Error) => {
          toast.error('Chyba: ' + e.message)
          this.loaded = true
        })
    },
    addMail() {
      this.router.push({
        name: 'Pridať zásielku',
      })
    },
    getMailStatuses() {
      MailDataService.getMailStatuses()
        .then((response: ResponseData) => {
          this.mailStatuses = response.data.data
        })
        .catch((e: Error) => {
          toast.error('Chyba: ' + e.message)
        })
    },
    getStatusName(status: number) {
      const found = this.mailStatuses.find((s) => s.id == status)
      return found ? found.description || found.name : status
    },
    allVisibleMailsSelected() {
      if (!this.mails?.length) return false
      return this.mails.every((mail: Mail) => this.selectedMailIds.includes(mail.id))
    },
    toggleSelectAllVisible(value: boolean | undefined | Event) {
      let checked: boolean | undefined
      if (value && typeof value === 'object' && 'target' in value) {
        checked = (value.target as HTMLInputElement).checked
      } else {
        checked = value as boolean | undefined
      }
      if (checked) {
        this.selectedMailIds = this.mails.map((mail: Mail) => mail.id)
      } else {
        this.selectedMailIds = []
      }
    },
    toggleMailSelection(mailId: number, checked: boolean) {
      if (checked) {
        if (!this.selectedMailIds.includes(mailId)) {
          this.selectedMailIds.push(mailId)
        }
      } else {
        this.selectedMailIds = this.selectedMailIds.filter((id) => id !== mailId)
      }
    },
    async applyBulkStatusUpdate() {
      if (!this.selectedMailIds.length) {
        toast.error('Chyba: Vyberte aspoň jednu zásielku.')
        return
      }
      if (!this.bulkSelectedStatus) {
        toast.error('Chyba: Vyberte stav pre hromadnú zmenu.')
        return
      }
      this.isBulkUpdating = true
      try {
        const statusId = Number(this.bulkSelectedStatus)
        const response = await MailDataService.bulkUpdateStatus(this.selectedMailIds, statusId)
        const updatedCount = response?.data?.data?.updated_count ?? this.selectedMailIds.length
        const alreadyInStatusCount = response?.data?.data?.already_in_status_count ?? 0
        toast.success(`Stav bol úspešne zmenený pre ${updatedCount} zásielok.`)
        if (alreadyInStatusCount > 0) {
          toast.info(`${alreadyInStatusCount} zásielok už malo tento stav.`)
        }
        this.selectedMailIds = []
        this.retrieveMails(this.pagination.current_page || 1, '')
      } catch (e: any) {
        toast.error('Chyba: Hromadná zmena stavu zlyhala.')
      } finally {
        this.isBulkUpdating = false
      }
    },
    setOrderResultsBy(column: string) {
      if (this.selectedColumn == column) {
        if (this.selectedDirection == 'desc') {
          this.selectedDirection = 'asc'
        } else {
          this.selectedDirection = 'desc'
        }
      } else {
        this.selectedDirection = 'asc'
      }
      this.selectedColumn = column
    },
  },
  computed: {
    bulkStatusOptions() {
      return [
        { value: '', label: 'Hromadná zmena stavu' },
        ...this.mailStatuses.map((s) => ({ value: String(s.id), label: s.description || s.name })),
      ]
    },
    orderedItems() {
      switch (this.selectedColumn) {
        case 'id':
          return _.orderBy(this.mails, ['id'], [this.selectedDirection.includes('asc') ? 'asc' : 'desc'])
        case 'sender':
          return _.orderBy(this.mails, ['sender'], [this.selectedDirection.includes('asc') ? 'asc' : 'desc'])
        case 'status':
          return _.orderBy(this.mails, [(mail: Mail) => this.getStatusName(mail.status)], [this.selectedDirection.includes('asc') ? 'asc' : 'desc'])
        case 'date':
          return _.orderBy(
            this.mails,
            ['updated_at'],
            [this.selectedDirection.includes('asc') ? 'asc' : 'desc'],
          )
        case 'name':
          return _.orderBy(this.mails, ['name'], [this.selectedDirection.includes('asc') ? 'asc' : 'desc'])
        default:
          return _.orderBy(this.mails, ['id'], ['desc'])
      }
    },
  },
  watch: {
    filterMails: {
      handler() {
        clearTimeout(this.searchTimer)
        this.searchTimer = setTimeout(() => {
          this.retrieveMails(1, '')
        }, 300)
      },
    },
  },
  mounted() {
    this.retrieveMails(1, '')
    this.getMailStatuses()
  },
})
</script>

<template>
  <main class="flex-1">
    <div class="py-6">
      <div class="max-w-(--breakpoint-2xl) mx-auto">
        <PageBreadcrumb page-title="Zoznam zásielok" />
      </div>
      <TableCard title="Zoznam zásielok">
        <template #controls>
          <div class="flex w-full items-center gap-3 sm:w-auto">
            <div class="flex-1 sm:w-64 sm:flex-initial md:w-72">
              <TableSearch v-model="filterMails" />
            </div>
            <div class="flex flex-row gap-2 shrink-0">
              <TableFilterDropdown
                :options="bulkStatusOptions"
                :model-value="bulkSelectedStatus"
                @update:model-value="bulkSelectedStatus = $event"
              />
              <Button
                :disabled="!selectedMailIds.length || !bulkSelectedStatus || isBulkUpdating"
                @click="applyBulkStatusUpdate()"
              >
                <CircleCheck />
                <span>{{ isBulkUpdating ? 'Ukladám...' : `Zmeniť stav (${selectedMailIds.length})` }}</span>
              </Button>
              <Button @click="addMail()">
                <Plus />
                <span>Pridať zásielku</span>
              </Button>
            </div>
          </div>
        </template>
          <div class="max-w-full overflow-x-auto min-w-0">
            <Table class="min-w-full divide-y divide-gray-100 dark:divide-gray-800">
              <TableHeader
                class="border-y border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900"
              >
                <TableRow>
                  <TableCell is-header class="px-4 py-3 text-center">
                    <Checkbox :checked="allVisibleMailsSelected()" @change="toggleSelectAllVisible" aria-label="Vybrať všetky" />
                  </TableCell>
                  <TableCell is-header class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400">
                    <span class="inline-flex items-center" :class="{ 'text-brand-500': selectedColumn == 'id' }">
                      ID
                      <button>
                        <svg @click="setOrderResultsBy('id')" xmlns="http://www.w3.org/2000/svg"
                          class="ml-1 size-3" aria-hidden="true" fill="currentColor" viewBox="0 0 320 512">
                          <path
                            d="M27.66 224h264.7c24.6 0 36.89-29.78 19.54-47.12l-132.3-136.8c-5.406-5.406-12.47-8.107-19.53-8.107c-7.055 0-14.09 2.701-19.45 8.107L8.119 176.9C-9.229 194.2 3.055 224 27.66 224zM292.3 288H27.66c-24.6 0-36.89 29.77-19.54 47.12l132.5 136.8C145.9 477.3 152.1 480 160 480c7.053 0 14.12-2.703 19.53-8.109l132.3-136.8C329.2 317.8 316.9 288 292.3 288z" />
                        </svg>
                      </button>
                    </span>
                  </TableCell>
                  <TableCell is-header class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400">
                    <span class="inline-flex items-center" :class="{ 'text-brand-500': selectedColumn == 'name' }">
                      Adresát
                      <button>
                        <svg @click="setOrderResultsBy('name')" xmlns="http://www.w3.org/2000/svg"
                          class="ml-1 size-3" aria-hidden="true" fill="currentColor" viewBox="0 0 320 512">
                          <path
                            d="M27.66 224h264.7c24.6 0 36.89-29.78 19.54-47.12l-132.3-136.8c-5.406-5.406-12.47-8.107-19.53-8.107c-7.055 0-14.09 2.701-19.45 8.107L8.119 176.9C-9.229 194.2 3.055 224 27.66 224zM292.3 288H27.66c-24.6 0-36.89 29.77-19.54 47.12l132.5 136.8C145.9 477.3 152.1 480 160 480c7.053 0 14.12-2.703 19.53-8.109l132.3-136.8C329.2 317.8 316.9 288 292.3 288z" />
                        </svg>
                      </button>
                    </span>
                  </TableCell>
                  <TableCell is-header class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400">
                    <span class="inline-flex items-center"
                      :class="{ 'text-brand-500': selectedColumn == 'sender' }">
                      Odosielateľ
                      <button>
                        <svg @click="setOrderResultsBy('sender')" xmlns="http://www.w3.org/2000/svg"
                          class="ml-1 size-3" aria-hidden="true" fill="currentColor" viewBox="0 0 320 512">
                          <path
                            d="M27.66 224h264.7c24.6 0 36.89-29.78 19.54-47.12l-132.3-136.8c-5.406-5.406-12.47-8.107-19.53-8.107c-7.055 0-14.09 2.701-19.45 8.107L8.119 176.9C-9.229 194.2 3.055 224 27.66 224zM292.3 288H27.66c-24.6 0-36.89 29.77-19.54 47.12l132.5 136.8C145.9 477.3 152.1 480 160 480c7.053 0 14.12-2.703 19.53-8.109l132.3-136.8C329.2 317.8 316.9 288 292.3 288z" />
                        </svg>
                      </button>
                    </span>
                  </TableCell>
                  <TableCell is-header class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400">
                    <span class="inline-flex items-center"
                      :class="{ 'text-brand-500': selectedColumn == 'status' }">
                      Stav
                      <button>
                        <svg @click="setOrderResultsBy('status')" xmlns="http://www.w3.org/2000/svg"
                          class="ml-1 size-3" aria-hidden="true" fill="currentColor" viewBox="0 0 320 512">
                          <path
                            d="M27.66 224h264.7c24.6 0 36.89-29.78 19.54-47.12l-132.3-136.8c-5.406-5.406-12.47-8.107-19.53-8.107c-7.055 0-14.09 2.701-19.45 8.107L8.119 176.9C-9.229 194.2 3.055 224 27.66 224zM292.3 288H27.66c-24.6 0-36.89 29.77-19.54 47.12l132.5 136.8C145.9 477.3 152.1 480 160 480c7.053 0 14.12-2.703 19.53-8.109l132.3-136.8C329.2 317.8 316.9 288 292.3 288z" />
                        </svg>
                      </button>
                    </span>
                  </TableCell>
                  <TableCell is-header class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400">
                    <span class="inline-flex items-center"> Súbor na scan </span>
                  </TableCell>
                  <TableCell is-header class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400">
                    <span class="inline-flex items-center" :class="{ 'text-brand-500': selectedColumn == 'date' }">
                      Dátum zmeny
                      <button>
                        <svg @click="setOrderResultsBy('date')" xmlns="http://www.w3.org/2000/svg"
                          class="ml-1 size-3" aria-hidden="true" fill="currentColor" viewBox="0 0 320 512">
                          <path
                            d="M27.66 224h264.7c24.6 0 36.89-29.78 19.54-47.12l-132.3-136.8c-5.406-5.406-12.47-8.107-19.53-8.107c-7.055 0-14.09 2.701-19.45 8.107L8.119 176.9C-9.229 194.2 3.055 224 27.66 224zM292.3 288H27.66c-24.6 0-36.89 29.77-19.54 47.12l132.5 136.8C145.9 477.3 152.1 480 160 480c7.053 0 14.12-2.703 19.53-8.109l132.3-136.8C329.2 317.8 316.9 288 292.3 288z" />
                        </svg>
                      </button>
                    </span>
                  </TableCell>
                  <TableCell is-header class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 text-right">Akcie</TableCell>
                  <TableCell is-header class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 text-right">Edit</TableCell>
                </TableRow>
              </TableHeader>
              <TableBody class="divide-y divide-gray-100 dark:divide-gray-800">
                <TableRow class="hover:bg-gray-200 dark:hover:bg-gray-700" :class="[mail.status != 1 && 'bg-gray-50']"
                  v-for="mail in orderedItems" :key="mail.id">
                  <TableCell class="px-4 py-4 text-center">
                    <Checkbox
                      :checked="selectedMailIds.includes(mail.id)"
                      @change="(checked) => toggleMailSelection(mail.id, checked)"
                      aria-label="Vybrať zásielku"
                    />
                  </TableCell>
                  <TableCell class="px-6 py-4 text-sm font-medium text-gray-800 dark:text-white/90">{{ mail.id }}</TableCell>
                  <TableCell class="px-6 py-4 text-sm text-gray-800 dark:text-white/90">{{ mail.name }}</TableCell>
                  <TableCell class="px-6 py-4 text-sm text-gray-800 dark:text-white/90">{{ mail.sender }}</TableCell>
                  <TableCell class="px-6 py-4 text-sm text-gray-800 dark:text-white/90">{{ getStatusName(mail.status) }}</TableCell>
                  <TableCell class="px-6 py-4 text-sm text-gray-800 dark:text-white/90">
                    <FormKit type="checkbox" id="scan-mail" disabled :modelValue="mail.scan != null ? true : false" name="scan-mail"
                      :classes="{ input: 'size-4 focus:ring-brand-500 focus:border-brand-500 block shadow-xs sm:text-sm border-gray-300 rounded-md text-brand-500 dark:border-gray-700' }" />
                  </TableCell>
                  <TableCell class="px-6 py-4 text-sm text-gray-800 dark:text-white/90">{{ mail.updated_at }}</TableCell>
                  <TableCell class="flex flex-row justify-end px-6 py-4 text-sm font-medium text-right">
                    <div v-if="mail.scan_requested">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                        stroke="currentColor" class="size-6">
                        <path stroke-linecap="round" stroke-linejoin="round"
                          d="M6.72 13.829c-.24.03-.48.062-.72.096m.72-.096a42.415 42.415 0 0110.56 0m-10.56 0L6.34 18m10.94-4.171c.24.03.48.062.72.096m-.72-.096L17.66 18m0 0l.229 2.523a1.125 1.125 0 01-1.12 1.227H7.231c-.662 0-1.18-.568-1.12-1.227L6.34 18m11.318 0h1.091A2.25 2.25 0 0021 15.75V9.456c0-1.081-.768-2.015-1.837-2.175a48.055 48.055 0 00-1.913-.247M6.34 18H5.25A2.25 2.25 0 013 15.75V9.456c0-1.081.768-2.015 1.837-2.175a48.041 48.041 0 011.913-.247m10.5 0a48.536 48.536 0 00-10.5 0m10.5 0V3.375c0-.621-.504-1.125-1.125-1.125h-8.25c-.621 0-1.125.504-1.125 1.125v3.659M18 10.5h.008v.008H18V10.5zm-3 0h.008v.008H15V10.5z" />
                      </svg>
                    </div>
                    <div v-if="mail.forward_requested">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                        stroke="currentColor" class="size-6">
                        <path stroke-linecap="round" stroke-linejoin="round"
                          d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                      </svg>
                    </div>
                    <div v-if="mail.shred_requested">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                        stroke="currentColor" class="size-6">
                        <path stroke-linecap="round" stroke-linejoin="round"
                          d="M9 4.5v15m6-15v15m-10.875 0h15.75c.621 0 1.125-.504 1.125-1.125V5.625c0-.621-.504-1.125-1.125-1.125H4.125C3.504 4.5 3 5.004 3 5.625v12.75c0 .621.504 1.125 1.125 1.125z" />
                      </svg>
                    </div>
                  </TableCell>
                  <TableCell class="px-6 py-4 text-sm font-medium text-right">
                    <router-link :to="'/mail-detail/' + mail.id"
                      class="text-green-500 hover:text-green-700">Edit</router-link>
                  </TableCell>
                </TableRow>
                <TableEmptyState
                  v-if="loaded && !mails?.length"
                  title="Žiadne zásielky neboli nájdené"
                  hint="Skúste upraviť hľadanie."
                  colspan="9"
                />
              </TableBody>
            </Table>
          </div>
          <template #footer>
            <TablePagination
              v-if="pagination.last_page > 1"
              :current-page="pagination.current_page"
              :total-pages="pagination.last_page"
              @page-change="retrieveMails($event, '')"
            />
          </template>
        </TableCard>
      </div>
    </main>
</template>
